import { useCallback, useRef, useEffect } from "react"
import { useDispatch } from "react-redux"
import { useAnalytics } from "connected-components/analytics"
import { Creators as DialogCreators } from "modules/dialog/model"
import { DASHBOARD_EVENTS } from "../analytics/events"
import {
  getPanelIdForModule,
  isModuleRemovable,
  buildAssetViewConfigForDuplicate,
  buildModuleConfigForDuplicate,
} from "../helpers/module-renderers"
import { getAssetViewCurrentView } from "../context/asset-view-current-view-registry"
import { getModuleDefinition } from "../modules/registry"
import { MODULE_TYPES, type DashboardModule, type ModuleType, type ModuleConfig } from "../modules/types"

const REMOVE_MODULE_DIALOG = {
  title: "Remove module?",
  contentText: "This cannot be undone. You will need to repin from the AI teammate if you want to add it again.",
  confirmText: "Delete",
} as const

export interface UseDashboardModuleActionsArgs {
  removeModule: (module: DashboardModule) => void
  updateModule: (moduleId: string, updates: Partial<DashboardModule>) => void
  addModule: (payload: { type: ModuleType, config?: ModuleConfig }) => string
}

export function useDashboardModuleActions(
  modules: DashboardModule[],
  { removeModule, updateModule, addModule }: UseDashboardModuleActionsArgs,
) {
  const dispatch = useDispatch()
  const analytics = useAnalytics()
  const modulesRef = useRef(modules)
  useEffect(() => {
    modulesRef.current = modules
  }, [modules])

  const handleRemoveModule = useCallback((moduleId: string, requiresConfirmation = true) => {
    const currentModules = modulesRef.current
    const module = currentModules.find((m) => m.id === moduleId)
    if (!module) return

    const definition = getModuleDefinition(module.type)
    if (!isModuleRemovable(module, definition)) return

    const performRemoval = () => {
      const latestModule = modulesRef.current.find((m) => m.id === moduleId)
      if (!latestModule) return
      analytics.track(DASHBOARD_EVENTS.MODULE_REMOVED, {
        moduleId: latestModule.id,
        moduleType: latestModule.type,
        kind: latestModule.config?.kind,
        pinnedId: latestModule.config?.pinnedId,
        isPinning: latestModule.config?.isPinning,
      })
      removeModule(latestModule)
    }

    if (requiresConfirmation) {
      dispatch(DialogCreators.open({
        title: REMOVE_MODULE_DIALOG.title,
        contentText: REMOVE_MODULE_DIALOG.contentText,
        buttons: [{ text: REMOVE_MODULE_DIALOG.confirmText, type: "danger", action: performRemoval }],
      }))
    } else {
      performRemoval()
    }
  }, [analytics, dispatch, removeModule])

  const handleUpdateModuleTitle = useCallback((moduleId: string, newTitle: string) => {
    updateModule(moduleId, { config: { title: newTitle } })
  }, [updateModule])

  const handleDuplicateModule = useCallback((moduleId: string) => {
    const currentModules = modulesRef.current
    const module = currentModules.find((m) => m.id === moduleId)
    if (!module) return

    const definition = getModuleDefinition(module.type)
    const baseTitle = (module.config?.title as string) || definition.title
    const sourcePanelId = getPanelIdForModule(module)
    const payload =
      module.type === MODULE_TYPES.ASSET_VIEW
        ? {
          type: MODULE_TYPES.ASSET_VIEW,
          config: buildAssetViewConfigForDuplicate(
            module,
            sourcePanelId,
            baseTitle,
            getAssetViewCurrentView(sourcePanelId),
          ),
        }
        : { type: module.type as ModuleType, config: buildModuleConfigForDuplicate(module, baseTitle) }
    addModule(payload)
    const config = payload.config ?? {}
    analytics.track(DASHBOARD_EVENTS.MODULE_ADDED, {
      moduleType: payload.type,
      title: config.title,
      initialTab: config.initialTab,
      initialView: config.initialView,
      isSecondary: config.isSecondary,
      kind: config.kind,
      pinnedId: config.pinnedId,
      isPinning: config.isPinning,
    })
  }, [addModule, analytics])

  return { handleRemoveModule, handleUpdateModuleTitle, handleDuplicateModule }
}
